import { commercialLinePresentation } from "../../../../shared/commercial-line-presentation";
type Category = { id: string; name: string; kind: string };
type Item = { id: string; categoryId: string; modifierType?: string | null; itemType?: string | null };
type Line = { itemName?: string | null; itemDescription?: string | null; description?: string; priceBookItemId?: string | null; groupName?: string | null; sourceItemType?: string | null; unitPriceCents?: number };
const labels = ["Labor / install", "Equipment", "Materials", "Other items", "Fees", "Rebates", "Discounts"];

/** Presentation only: preserve line identity, amounts, and order within each group. */
export function groupInvoiceLines<T extends Line>(lines: readonly T[], items: readonly Item[] = [], categories: readonly Category[] = []): Array<[string, T[]]> {
  const itemsById = new Map(items.map((item) => [item.id, item]));
  const categoriesById = new Map(categories.map((category) => [category.id, category]));
  const byName = new Map<string, Set<string>>();
  for (const category of categories) {
    const name = category.name.trim().toLowerCase();
    const kinds = byName.get(name) ?? new Set<string>();
    kinds.add(category.kind);
    byName.set(name, kinds);
  }
  const groups: T[][] = labels.map(() => []);
  for (const line of lines) {
    const item = line.priceBookItemId ? itemsById.get(line.priceBookItemId) : undefined;
    const category = item ? categoriesById.get(item.categoryId) : undefined;
    const name = line.groupName?.trim().toLowerCase() ?? "";
    const kinds = byName.get(name);
    // The document endpoint supplies category names; ambiguous names stay unclassified.
    const sourceKind = line.sourceItemType?.trim().toLowerCase();
    const explicitKind = item?.itemType ?? (["service", "equipment", "material"].includes(sourceKind ?? "") ? sourceKind : undefined);
    const kind = item?.modifierType ?? explicitKind ?? (/^fees?$/.test(name) ? "fee" : /^discounts?$/.test(name) ? "discount" : undefined) ?? category?.kind ?? (kinds?.size === 1 ? [...kinds][0] : undefined);
    const categoryName = category?.name ?? name;
    const deduction = (line.unitPriceCents ?? 0) < 0 || kind === "discount" || /rebate|promo|discount/i.test(categoryName);
    const rank = deduction ? (/rebate|promo/i.test(categoryName) ? 5 : 6) : kind === "service" ? 0 : kind === "equipment" ? 1 : kind === "material" ? 2 : kind === "fee" ? 4 : kind === "discount" ? 6
      : !kind && !kinds && /^(labor|labour|install|installation|services?)(\s*\/\s*(install|installation))?$/.test(name) ? 0
      : !kind && !kinds && name === "equipment" ? 1
      : !kind && !kinds && /^materials?$/.test(name) ? 2 : 3;
    groups[rank].push(line);
  }
  return groups.flatMap((group, index) => group.length ? [[labels[index], group] as [string, T[]]] : []);
}

/** Field invoices retain the company's pricebook category names. */
export function groupFieldInvoiceLines<T extends Line>(lines: readonly T[]): Array<[string, T[]]> {
  const groups: Array<[string,T[]]> = [];
  for (const line of lines) {
    const display=commercialLinePresentation(line.itemName,line.description,line.itemDescription);
    const name=display.name==='HVAC Labor' ? display.name : (/^uncategorized imported items$/i.test(line.groupName?.trim() ?? '') ? (line.sourceItemType?.toLowerCase() === 'service' ? 'Services' : 'Invoice items') : line.groupName?.trim()) || 'Other charges';
    const previous=groups.at(-1);
    if(previous?.[0]===name) previous[1].push(line);
    else groups.push([name,[line]]);
  }
  return groups;
}
